import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  Stack,
  Paper
} from '@mui/material';
import {
  Login as LoginIcon,
  Close as CloseIcon,
  Api as ApiIcon
} from '@mui/icons-material';
import { WalteroTypography, WalteroButton } from './waltero';

const WalteroLandingPage = ({ onLogin, loginLoading, loginError }) => {
  const theme = useTheme();
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });

  const performLogin = async () => {
    try {
      // Pass the loginForm data to the parent's handleLogin function
      const success = await onLogin(loginForm);
      
      // Only close modal if login was successful
      if (success) {
        setLoginOpen(false);
        setLoginForm({ username: '', password: '' });
      }
      // If login failed, modal stays open and error will be displayed
    } catch (error) {
      console.error('Error in performLogin:', error);
      // Keep modal open on error
    }
  };

  const handleLogin = async () => {
    try {
      await performLogin();
    } catch (error) {
      console.error('Login error in handleLogin:', error);
      // Don't close the modal on error
    }
  };

  // Hero Section Component
  const HeroSection = () => (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.waltero.primary.deepBlue} 0%, ${theme.palette.waltero.primary.coreBlue} 50%, ${theme.palette.waltero.primary.mediumBlue} 100%)`,
        color: 'white',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 30% 20%, ${theme.palette.waltero.secondary.primaryApricot}15 0%, transparent 50%)`,
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box sx={{ mb: 3 }}>
              <Chip
                label="AI-POWERED MONITORING"
                sx={{
                  bgcolor: theme.palette.waltero.secondary.primaryApricot,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  mb: 3
                }}
              />
            </Box>
            
            <WalteroTypography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                fontWeight: 800,
                mb: 3,
                lineHeight: 1.1,
                background: `linear-gradient(45deg, white 30%, ${theme.palette.waltero.secondary.lightApricot} 90%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Unparalleled Visibility into Your Critical&nbsp;Assets
            </WalteroTypography>
            
            <WalteroTypography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.95,
                fontWeight: 400,
                lineHeight: 1.4,
                maxWidth: '600px'
              }}
            >
              Monitor utilities, water systems, gas networks, and district heating with ultra-low power IoT sensors. 
              Get 10-year battery life and real-time insights that prevent costly failures.
            </WalteroTypography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
              <WalteroButton
                variant="contained"
                size="large"
                onClick={() => setLoginOpen(true)}
                startIcon={<LoginIcon />}
                sx={{
                  bgcolor: theme.palette.waltero.secondary.primaryApricot,
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(241, 136, 73, 0.3)',
                  '&:hover': {
                    bgcolor: theme.palette.waltero.secondary.darkApricot,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(195, 93, 44, 0.4)',
                  }
                }}
              >
                Access Dashboard
              </WalteroButton>
              <WalteroButton
                variant="outlined"
                size="large"
                onClick={() => window.open('https://www.waltero.com', '_blank')}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderColor: theme.palette.waltero.secondary.lightApricot,
                    color: theme.palette.waltero.secondary.lightApricot
                  }
                }}
              >
                Learn More
              </WalteroButton>
            </Stack>

            {/* Trust indicators */}
            <Stack direction="row" spacing={4} sx={{ opacity: 0.9 }}>
              <Box>
                <WalteroTypography variant="h6" sx={{ fontWeight: 700, color: theme.palette.waltero.secondary.lightApricot }}>
                  10+ Years
                </WalteroTypography>
                <WalteroTypography variant="caption">Battery Life</WalteroTypography>
              </Box>
              <Box>
                <WalteroTypography variant="h6" sx={{ fontWeight: 700, color: theme.palette.waltero.secondary.lightApricot }}>
                  99.9%
                </WalteroTypography>
                <WalteroTypography variant="caption">Uptime</WalteroTypography>
              </Box>
              <Box>
                <WalteroTypography variant="h6" sx={{ fontWeight: 700, color: theme.palette.waltero.secondary.lightApricot }}>
                  Real-time
                </WalteroTypography>
                <WalteroTypography variant="caption">Monitoring</WalteroTypography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                position: 'relative',
                textAlign: 'center',
                '& > *': {
                  animation: 'float 3s ease-in-out infinite',
                }
              }}
            >
              {/* Dashboard Preview Mockup */}
              <Paper
                elevation={8}
                sx={{
                  bgcolor: 'white',
                  borderRadius: '16px',
                  p: 3,
                  maxWidth: '400px',
                  mx: 'auto',
                  border: `2px solid ${theme.palette.waltero.secondary.paleApricot}`,
                  boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                  transform: 'perspective(1000px) rotateY(-5deg) rotateX(5deg)',
                }}
              >
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: theme.palette.waltero.fleetStatus.success 
                  }} />
                  <WalteroTypography variant="caption" sx={{ color: theme.palette.waltero.primary.coreBlue, fontWeight: 600 }}>
                    LIVE MONITORING
                  </WalteroTypography>
                </Box>
                
                <WalteroTypography variant="h6" sx={{ color: theme.palette.waltero.primary.deepBlue, mb: 2, fontWeight: 700 }}>
                  Fleet Overview
                </WalteroTypography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: theme.palette.waltero.secondary.backgroundApricot, borderRadius: 2 }}>
                      <WalteroTypography variant="h5" sx={{ color: theme.palette.waltero.fleetStatus.success, fontWeight: 700 }}>
                        2,847
                      </WalteroTypography>
                      <WalteroTypography variant="caption" sx={{ color: theme.palette.waltero.primary.mediumBlue }}>
                        Active Devices
                      </WalteroTypography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: theme.palette.waltero.primary.backgroundBlue, borderRadius: 2 }}>
                      <WalteroTypography variant="h5" sx={{ color: theme.palette.waltero.secondary.primaryApricot, fontWeight: 700 }}>
                        98.6%
                      </WalteroTypography>
                      <WalteroTypography variant="caption" sx={{ color: theme.palette.waltero.primary.mediumBlue }}>
                        Health Score
                      </WalteroTypography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2, height: '40px', bgcolor: theme.palette.waltero.primary.neutralBlue, borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    height: '100%', 
                    width: '75%', 
                    bgcolor: theme.palette.waltero.secondary.primaryApricot,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <WalteroTypography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                      Performance Trend ↗
                    </WalteroTypography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: perspective(1000px) rotateY(-5deg) rotateX(5deg) translateY(0px); }
          50% { transform: perspective(1000px) rotateY(-5deg) rotateX(5deg) translateY(-10px); }
        }
      `}</style>
    </Box>
  );




  return (
    <Box sx={{ bgcolor: theme.palette.waltero.primary.backgroundBlue, minHeight: '100vh' }}>
      {/* Hero Section */}
      <HeroSection />


      {/* Login Dialog */}
      <Dialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: `2px solid ${theme.palette.waltero.secondary.paleApricot}`
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <WalteroTypography variant="h5" sx={{ fontWeight: 700, color: theme.palette.waltero.primary.deepBlue }}>
              Access Your Dashboard
            </WalteroTypography>
            <IconButton
              onClick={() => setLoginOpen(false)}
              sx={{ color: theme.palette.waltero.primary.mediumBlue }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <WalteroTypography variant="body2" sx={{ color: theme.palette.waltero.primary.mediumBlue, mt: 1 }}>
            Sign in to monitor your infrastructure assets
          </WalteroTypography>
        </DialogTitle>
        
        <Box>
          <DialogContent sx={{ pt: 2 }}>
            {loginError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                {loginError}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Username"
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              margin="normal"
              required
              variant="outlined"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              margin="normal"
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                }
              }}
            />
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <WalteroButton
              onClick={() => setLoginOpen(false)}
              sx={{
                color: theme.palette.waltero.primary.mediumBlue,
                fontWeight: 600,
                textTransform: 'none',
                px: 3
              }}
            >
              Cancel
            </WalteroButton>
            <WalteroButton
              onClick={handleLogin}
              variant="contained"
              disabled={loginLoading}
              sx={{
                bgcolor: theme.palette.waltero.secondary.primaryApricot,
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: theme.palette.waltero.secondary.darkApricot
                }
              }}
            >
              {loginLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
            </WalteroButton>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default WalteroLandingPage;